import type { App, BlockDef } from '@cogo/types-v2'
import { loadBlock as loadBlockV2 } from '@cogo/runtime-v2'

/**
 * Walk an element tree and return the deduped set of `registry:*` $ref values.
 * Used by the registry prefetch path; only the `registry:` scheme is returned,
 * internal `#/definitions/*` JSON-schema refs are ignored.
 */
export function discoverRegistryRefs(elements: unknown[]): string[] {
  const refs = new Set<string>()
  const walk = (nodes: unknown): void => {
    if (!Array.isArray(nodes)) return
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue
      const n = node as { $ref?: unknown; children?: unknown }
      if (typeof n.$ref === 'string' && n.$ref.startsWith('registry:')) {
        refs.add(n.$ref)
      }
      if (n.children) walk(n.children)
    }
  }
  walk(elements)
  return [...refs]
}

export interface ThemeTokens {
  colors?: Record<string, string>
  radius?: string
}

export interface LoadedApp {
  manifest: App
  blocks: Record<string, BlockDef>
  /** Load additional blocks on demand (for route changes) */
  loadBlocks: (names: string[]) => Promise<void>
  /** Promise that resolves when all blocks (background) finish loading */
  backgroundReady: Promise<void>
  /** Theme tokens loaded from themePath — CSS variable overrides */
  themeTokens?: ThemeTokens
}

function isJsonResponse(res: Response): boolean {
  const ct = res.headers.get('content-type') ?? ''
  return ct.includes('application/json')
}

/** Fetch-based FileReader for v2 block loader */
function fetchFileReader(url: string): Promise<unknown> {
  return fetch(`${url}?_t=${Date.now()}`).then((res) => {
    if (!res.ok) throw new Error(`${res.status}`)
    const ct = res.headers.get('content-type') ?? ''
    if (!ct.includes('application/json')) throw new Error('Not JSON')
    return res.json()
  })
}

/**
 * Load v2 app manifest + blocks for the current route only.
 * Other blocks are loaded on demand via loadBlocks().
 */
export async function loadApp(basePath: string, currentRoute?: string): Promise<LoadedApp> {
  const manifestRes = await fetch(`${basePath}/app.json?_t=${Date.now()}`)
  if (!manifestRes.ok) throw new Error(`Failed to load manifest: ${manifestRes.status}`)
  const manifest: App = await manifestRes.json()

  const blocks: Record<string, BlockDef> = {}
  const loadedSet = new Set<string>()

  // Determine which blocks to load initially:
  // globalBlocks + blocks for matching page(s) + their layouts
  const initialBlocks = new Set<string>()
  if (manifest.globalBlocks) {
    for (const b of manifest.globalBlocks) initialBlocks.add(b)
  }

  // Find pages matching the current route
  const route = currentRoute ?? toManifestRoute(window.location.pathname, basePath)
  for (const page of manifest.pages) {
    if (routeMatches(page.route, route)) {
      for (const b of page.blocks) initialBlocks.add(b)
      // Also load layout block if specified
      if (page.layout && page.layout !== 'stack') {
        initialBlocks.add(page.layout)
      }
    }
  }

  // If no pages matched (e.g., login page not in manifest), load first few pages' blocks
  if (initialBlocks.size === (manifest.globalBlocks?.length ?? 0)) {
    for (const page of manifest.pages.slice(0, 3)) {
      for (const b of page.blocks) initialBlocks.add(b)
    }
  }

  // Load initial blocks with concurrency control
  await loadBlockBatch(basePath, Array.from(initialBlocks), blocks, loadedSet)

  // Background load remaining blocks (non-blocking)
  const allBlockNames = collectAllBlockNames(manifest)
  const remaining = allBlockNames.filter(n => !loadedSet.has(n))
  const backgroundReady = remaining.length > 0
    ? loadBlockBatch(basePath, remaining, blocks, loadedSet).catch(() => {})
    : Promise.resolve()

  // Load theme tokens from themePath (optional — CSS variable overrides)
  let themeTokens: ThemeTokens | undefined
  const themePath = (manifest as unknown as Record<string, string>).themePath
  if (themePath) {
    try {
      const themeRes = await fetch(`${basePath}/${themePath}?_t=${Date.now()}`)
      if (themeRes.ok && isJsonResponse(themeRes)) {
        themeTokens = await themeRes.json()
      }
    } catch {
      // theme JSON is optional
    }
  }

  return {
    manifest,
    blocks,
    loadBlocks: async (names: string[]) => {
      const toLoad = names.filter(n => !loadedSet.has(n))
      if (toLoad.length > 0) {
        await loadBlockBatch(basePath, toLoad, blocks, loadedSet)
      }
    },
    backgroundReady,
    themeTokens,
  }
}

/** Collect all unique block names from manifest */
function collectAllBlockNames(manifest: App): string[] {
  const names = new Set<string>()
  if (manifest.globalBlocks) manifest.globalBlocks.forEach(b => names.add(b))
  for (const page of manifest.pages) {
    page.blocks.forEach(b => names.add(b))
    // Include layout blocks
    if (page.layout && page.layout !== 'stack') {
      names.add(page.layout)
    }
  }
  return Array.from(names)
}

/** Convert browser pathname to manifest route */
function toManifestRoute(pathname: string, basePath: string): string {
  if (pathname.startsWith(basePath)) {
    const rest = pathname.slice(basePath.length)
    return rest === '' ? '/' : rest
  }
  return pathname
}

/** Check if a page route matches the current route */
function routeMatches(pageRoute: string, currentRoute: string): boolean {
  if (pageRoute === currentRoute) return true
  if (currentRoute.startsWith(pageRoute + '/')) return true
  if (pageRoute.startsWith(currentRoute + '/')) return true
  return false
}

/** Load a batch of blocks with concurrency control */
async function loadBlockBatch(
  basePath: string,
  names: string[],
  blocks: Record<string, BlockDef>,
  loadedSet: Set<string>,
): Promise<void> {
  const CONCURRENCY = 6
  const queue = [...names]

  while (queue.length > 0) {
    const batch = queue.splice(0, CONCURRENCY)
    await Promise.all(
      batch.map(async (blockName) => {
        if (loadedSet.has(blockName)) return
        loadedSet.add(blockName)
        try {
          const blockDef = await loadBlockV2(`${basePath}/blocks/${blockName}`, fetchFileReader)
          blocks[blockName] = blockDef
        } catch (err) {
          console.warn(`[v2] Failed to load block: ${blockName}`, err)
        }
      }),
    )
  }
}
